// Export to Excel for the phased burn tool, laid out the way Aeron reworked
// the February export (Burn tool export template - AG.xlsx, 11/3): numbers
// as numbers with Excel formats, the phase totals as formulas over the
// block, Estimated Cost and Estimated Hours as formulas from the ETC week
// on (so editing a Burn Rate / Hours/Week / Crew Size cell moves the phase's
// ETC in columns H and I), the current week highlighted, the editable cells
// outlined, and a Pre-Adjust Backup sheet holding the same data to compare
// against after editing. The upload (burnToolPhasedUpload.js) reads the
// first sheet back; it only needs the Phase Code / Data Type columns and
// the week headers, which have not moved.
import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import moment from 'moment';

export const SHEET_NAME = 'Burn Tool Phased Data';
export const BACKUP_SHEET_NAME = 'Pre-Adjust Backup';
export const MANPOWER_SHEET_NAME = 'Manpower Curve';
export const ETC_LABEL = 'ETC From Week';

export const BASE_HEADERS = [
    'Phase Code',
    'Phase Description',
    'Data Type',
    'Phase Budget Dollars',
    'Phase Budget Hours',
    'Phase Actual Total Dollars',
    'Phase Actual Total Hours',
    'Phase ETC Dollars',
    'Phase ETC Hours',
];

// One block of nine rows per phase, in this order. offset is the row's
// place in the block; the formulas below count on it.
export const DATA_TYPES = [
    { key: 'estimatedCost', label: 'Estimated Cost', fmt: 'int', bold: true },
    { key: 'burnRate', label: 'Burn Rate', fmt: 'dec2', input: true },
    { key: 'hoursWeek', label: 'Hours/Week', fmt: 'int0', input: true },
    { key: 'crewSize', label: 'Crew Size', fmt: 'dec2', input: true },
    { key: 'actualCost', label: 'Actual Cost', fmt: 'int', bold: true },
    { key: 'actualBurnRate', label: 'Actual Burn Rate', fmt: 'dec2' },
    { key: 'actualHours', label: 'Actual Hours', fmt: 'int', bold: true },
    { key: 'estimatedHours', label: 'Estimated Hours', fmt: 'int', bold: true },
    { key: 'betweenInterval', label: 'Between Date Interval', fmt: 'text' },
];
const ROW = Object.fromEntries(DATA_TYPES.map((t, i) => [t.key, i]));

const NUM_FMT = {
    int: '#,##0;\\(#,##0\\);\\-',
    int0: '0',
    dec2: '0.00',
    text: 'General',
};

const INK = 'FF262626';
const GREY_TEXT = 'FF595959';
const HEADER_FILL = 'FFF2F2F2';
const THIN = { style: 'thin', color: { argb: 'FFBFBFBF' } };
const BORDER = { top: THIN, left: THIN, bottom: THIN, right: THIN };
const font = (extra = {}) => ({ name: 'Calibri', size: 10, color: { argb: INK }, ...extra });
const solid = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });

export const colLetter = (n) => {
    let s = '';
    for (let x = n; x > 0; x = Math.floor((x - 1) / 26)) s = String.fromCharCode(65 + ((x - 1) % 26)) + s;
    return s;
};

// The first week the tool counts toward ETC: after the start of the
// version's week (the rule in burnToolPhased's ETC totals and in the
// upload's diff). Null when no week qualifies.
export const etcFromWeek = (weeks, versionStart) => {
    if (!versionStart) return null;
    const start = moment(versionStart).startOf('week');
    return [...(weeks || [])].sort().find(w => moment(w).isAfter(start)) || null;
};

const n = (v) => (typeof v === 'number' && !isNaN(v) ? v : 0);
const orBlank = (v) => (n(v) ? n(v) : '');

// phases: [{ code, description, budgetDollars, budgetHours,
//            weeks: { [week]: { rate, hours, crew, actualCost, actualHours, between } } }]
// weeks:  the week keys, any order
// etcWeek: the first ETC week (etcFromWeek), or null
// phases may also carry discipline (the WBS section the phase rolls up to
// on the Manpower Curve sheet). The charts on that sheet are recorded on
// the workbook as burnToolCharts; writeBurnToolPhasedXlsx puts them in the
// file, since ExcelJS cannot draw a chart itself.
export const buildBurnToolPhasedWorkbook = ({ phases, weeks, etcWeek, hoursPerWeek }) => {
    const wb = new ExcelJS.Workbook();
    wb.calcProperties.fullCalcOnLoad = true;
    const sortedWeeks = [...(weeks || [])].sort();
    writeSheet(wb.addWorksheet(SHEET_NAME), phases, sortedWeeks, etcWeek);
    writeSheet(wb.addWorksheet(BACKUP_SHEET_NAME), phases, sortedWeeks, etcWeek);
    wb.burnToolCharts = writeManpowerSheet(wb.addWorksheet(MANPOWER_SHEET_NAME), phases, sortedWeeks, hoursPerWeek);
    return wb;
};

// The row on the data sheet where phase i's block starts (its Estimated Cost row).
export const blockRow = (i) => 2 + i * DATA_TYPES.length;

const writeSheet = (ws, phases, weeks, etcWeek) => {
    const firstWeekCol = BASE_HEADERS.length + 1;             // J
    const lastWeekCol = BASE_HEADERS.length + weeks.length;   // BB in a 45-week job
    const etcLabelCol = lastWeekCol + 2;
    const etcCol = lastWeekCol + 3;
    const J = colLetter(firstWeekCol), L = colLetter(lastWeekCol), E = colLetter(etcCol);
    const weekLetters = weeks.map((_, i) => colLetter(firstWeekCol + i));
    const isEtc = (week) => !!etcWeek && week >= etcWeek;

    // Header
    const header = ws.addRow([...BASE_HEADERS, ...weeks]);
    header.height = 35.5;
    for (let c = 1; c <= lastWeekCol; c++) {
        const cell = header.getCell(c);
        cell.font = font({ bold: true });
        cell.fill = solid(HEADER_FILL);
        cell.alignment = { horizontal: 'center', vertical: 'center', wrapText: true };
        cell.border = BORDER;
    }
    const etcLabel = header.getCell(etcLabelCol);
    etcLabel.value = ETC_LABEL;
    etcLabel.font = { name: 'Calibri', size: 11, bold: true };
    etcLabel.fill = solid('FFE0E0E0');
    etcLabel.alignment = { horizontal: 'center' };
    const etcCell = header.getCell(etcCol);
    etcCell.value = etcWeek || '';
    etcCell.numFmt = '@';
    etcCell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF0000FF' } };
    etcCell.fill = solid('FFFFFF00');
    etcCell.alignment = { horizontal: 'center' };

    // Phase blocks
    (phases || []).forEach((phase) => {
        const r0 = ws.rowCount + 1;
        const r = (key) => r0 + ROW[key];
        const byWeek = phase.weeks || {};

        // Cached results for the block's formulas, so a reader that does not
        // recalculate (and the upload's preview) still sees numbers.
        let actualCostTotal = 0, actualHoursTotal = 0, etcDollars = 0, etcHours = 0;
        weeks.forEach((week) => {
            const w = byWeek[week] || {};
            actualCostTotal += n(w.actualCost);
            actualHoursTotal += n(w.actualHours);
            if (isEtc(week) && n(w.rate) && n(w.hours) && n(w.crew)) {
                etcDollars += n(w.rate) * n(w.hours) * n(w.crew);
                etcHours += n(w.hours) * n(w.crew);
            }
        });

        DATA_TYPES.forEach((type, i) => {
            const rowNo = r0 + i;
            const row = ws.getRow(rowNo);
            row.getCell(1).value = String(phase.code);
            row.getCell(2).value = phase.description || '';
            row.getCell(3).value = type.label;

            if (i === 0) {
                row.getCell(4).value = n(phase.budgetDollars);
                row.getCell(5).value = n(phase.budgetHours);
                row.getCell(6).value = { formula: `SUM(${J}${r('actualCost')}:${L}${r('actualCost')})`, result: actualCostTotal };
                row.getCell(7).value = { formula: `SUM(${J}${r('actualHours')}:${L}${r('actualHours')})`, result: actualHoursTotal };
                row.getCell(8).value = { formula: `SUMPRODUCT(--($${J}$1:$${L}$1>=$${E}$1),${J}${r('estimatedCost')}:${L}${r('estimatedCost')})`, result: etcDollars };
                row.getCell(9).value = { formula: `SUMPRODUCT(--($${J}$1:$${L}$1>=$${E}$1),${J}${r('estimatedHours')}:${L}${r('estimatedHours')})`, result: etcHours };
            } else {
                const top = ws.getRow(r0);
                for (let c = 4; c <= 9; c++) {
                    const v = top.getCell(c).value;
                    row.getCell(c).value = { formula: `${colLetter(c)}$${r0}`, result: typeof v === 'object' && v !== null ? v.result : v };
                }
            }

            weeks.forEach((week, wi) => {
                const w = byWeek[week] || {};
                const col = weekLetters[wi];
                const rate = n(w.rate), hours = n(w.hours), crew = n(w.crew);
                const all = rate && hours && crew;
                const cost = all ? rate * hours * crew : 0;
                const estHours = all ? hours * crew : 0;
                const blankIfUnset = `OR(${col}${r('burnRate')}="",${col}${r('hoursWeek')}="",${col}${r('crewSize')}="")`;
                let value = '';
                switch (type.key) {
                    case 'estimatedCost':
                        value = isEtc(week)
                            ? { formula: `IF(${blankIfUnset},"",${col}${r('burnRate')}*${col}${r('hoursWeek')}*${col}${r('crewSize')})`, result: orBlank(cost) }
                            : orBlank(cost);
                        break;
                    case 'burnRate': value = orBlank(rate); break;
                    case 'hoursWeek': value = orBlank(hours); break;
                    case 'crewSize': value = orBlank(crew); break;
                    case 'actualCost': value = orBlank(w.actualCost); break;
                    case 'actualBurnRate': value = n(w.actualCost) && n(w.actualHours) ? n(w.actualCost) / n(w.actualHours) : ''; break;
                    case 'actualHours': value = orBlank(w.actualHours); break;
                    case 'estimatedHours':
                        value = isEtc(week)
                            ? { formula: `IF(${blankIfUnset},"",${col}${r('hoursWeek')}*${col}${r('crewSize')})`, result: orBlank(estHours) }
                            : orBlank(estHours);
                        break;
                    case 'betweenInterval': value = w.between ? 'Yes' : 'No'; break;
                    default: value = '';
                }
                row.getCell(firstWeekCol + wi).value = value;
            });

            // Styles
            for (let c = 1; c <= lastWeekCol; c++) {
                const cell = row.getCell(c);
                cell.border = BORDER;
                cell.font = font({ color: { argb: c <= 2 ? GREY_TEXT : INK }, bold: c === 3 && !!type.bold });
                cell.alignment = { horizontal: c <= 2 ? 'left' : 'center', vertical: 'center' };
                if (c === 3) cell.fill = solid(HEADER_FILL);
                if (c >= 4 && c <= 9) cell.numFmt = NUM_FMT.int;
                if (c >= firstWeekCol) cell.numFmt = NUM_FMT[type.fmt];
            }
        });
    });

    const lastRow = ws.rowCount;

    // Widths
    [8, 18, 13, 10, 10, 10, 10, 10, 10].forEach((w, i) => { ws.getColumn(i + 1).width = w; });
    weeks.forEach((_, i) => { ws.getColumn(firstWeekCol + i).width = 11.5; });
    ws.getColumn(lastWeekCol + 1).width = 3;
    ws.getColumn(etcLabelCol).width = 15;
    ws.getColumn(etcCol).width = 12;

    // Header row and the phase / totals columns stay put.
    ws.views = [{ state: 'frozen', xSplit: BASE_HEADERS.length, ySplit: 1, topLeftCell: `${J}2` }];

    if (!weeks.length) return;
    const thisWeek = `TEXT(${J}$1,"yyyy-mm-dd")=TEXT(TODAY()-WEEKDAY(TODAY(),3),"yyyy-mm-dd")`;
    const navy = { argb: 'FF1F4E79' };
    // The current week's header
    ws.addConditionalFormatting({
        ref: `${J}1:${L}1`,
        rules: [{ type: 'expression', priority: 1, formulae: [thisWeek], style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFDDEBF7' } }, font: { bold: true, color: navy } } }],
    });
    // The current week's column, outlined
    ws.addConditionalFormatting({
        ref: `${J}2:${L}${lastRow}`,
        rules: [{ type: 'expression', priority: 2, formulae: [thisWeek], style: { border: { left: { style: 'thin', color: navy }, right: { style: 'thin', color: navy } } } }],
    });
    // The cells a person may edit, where they hold a value
    ws.addConditionalFormatting({
        ref: `${J}2:${L}${lastRow}`,
        rules: [{ type: 'expression', priority: 3, formulae: [`AND(OR($C2="Burn Rate",$C2="Hours/Week",$C2="Crew Size"),${J}2<>"")`], style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFFAFBFD' } }, border: { top: { style: 'thin', color: navy }, left: { style: 'thin', color: navy }, bottom: { style: 'thin', color: navy }, right: { style: 'thin', color: navy } } } }],
    });
};

// ---------------------------------------------------------------------------
// Manpower Curve: crew (FTE) per phase per week, read off the data sheet:
// the Crew Size row from the ETC week on, Actual Hours / hours-per-week
// before it. Then a rollup by discipline and a total, which the two charts
// under the table draw (stacked area by discipline, line of the total).
// Aeron's sheet (11/3) rolled up by the first two digits of the code with
// names typed in; here the discipline comes with the phase.

const FTE_FMT = '0.0;\\(0.0\\);\\-';
export const DEFAULT_HOURS_PER_WEEK = 60;

// The hours/week most of the plan uses, so actual hours convert to FTE the
// way the plan counts a person. 60 when the plan is empty.
export const planHoursPerWeek = (phases) => {
    const counts = {};
    (phases || []).forEach(p => Object.values(p.weeks || {}).forEach((w) => {
        const h = n(w.hours);
        if (h > 0) counts[h] = (counts[h] || 0) + 1;
    }));
    const top = Object.keys(counts).sort((a, b) => counts[b] - counts[a] || Number(a) - Number(b))[0];
    return top ? Number(top) : DEFAULT_HOURS_PER_WEEK;
};

export const disciplineLabel = (phase) => {
    const d = (phase.discipline || '').trim();
    if (d) return d;
    const code = String(phase.code || '');
    return code.length >= 2 ? `Codes ${code.slice(0, 2)}xxxx` : 'Other';
};

// Series colors in the order the Strike chart palette gives them, then a few
// more for a job with many disciplines.
const SERIES_COLORS = ['1D518C', '3F8FD9', 'D2691E', '8D5FB3', '0B8A6F', 'A3A3A3', '6F93BD', 'E07A7F', '88AED8', 'C9A227', '5A8CC4', '737373'];

const writeManpowerSheet = (ws, phases, weeks, hoursPerWeek) => {
    const DATA = `'${SHEET_NAME}'`;
    const dataFirstWeekCol = BASE_HEADERS.length + 1;
    const dataEtcCol = BASE_HEADERS.length + weeks.length + 3;
    const firstWeekCol = 4;                               // D
    const lastWeekCol = 3 + weeks.length;
    const peakCol = lastWeekCol + 1;
    const D = colLetter(firstWeekCol), L = colLetter(lastWeekCol);
    const headerRow = 5, firstPhaseRow = 6;
    const lastPhaseRow = firstPhaseRow + Math.max((phases || []).length, 1) - 1;
    const totalRow = lastPhaseRow + 1;
    const bold = { name: 'Calibri', size: 10, bold: true };
    const plain = { name: 'Calibri', size: 10 };
    const headerStyle = (cell) => { cell.font = bold; cell.fill = solid(HEADER_FILL); cell.alignment = { horizontal: 'center', vertical: 'center', wrapText: true }; cell.border = BORDER; };

    ws.getCell('A1').value = 'Manpower Curve by Phase (crew size / FTE per week)';
    ws.getCell('A1').font = { name: 'Calibri', size: 12, bold: true };
    ws.getCell('A2').value = 'Hrs/Week to convert actual hours to FTE';
    ws.getCell('A2').font = plain;
    ws.getCell('B2').value = hoursPerWeek || DEFAULT_HOURS_PER_WEEK;
    ws.getCell('B2').fill = solid('FFFFFF00');
    ws.getCell('B2').font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF0000FF' } };
    ws.getCell('A3').value = 'ETC From Week (forecast starts)';
    ws.getCell('A3').font = plain;
    ws.getCell('B3').value = { formula: `${DATA}!${colLetter(dataEtcCol)}1` };
    ws.getCell('B3').numFmt = '@';
    ws.getCell('A4').value = `Before the ETC week a phase's FTE is its actual hours / B2; from the ETC week on it is the Crew Size row of ${SHEET_NAME}.`;
    ws.getCell('A4').font = { name: 'Calibri', size: 9, italic: true, color: { argb: GREY_TEXT } };

    // Header
    const header = ws.getRow(headerRow);
    header.values = ['Phase Code', 'Phase Description', 'Discipline', ...weeks, 'Peak'];
    header.height = 30;
    for (let c = 1; c <= peakCol; c++) headerStyle(header.getCell(c));

    // One row per phase
    (phases || []).forEach((phase, i) => {
        const row = ws.getRow(firstPhaseRow + i);
        const crewRow = blockRow(i) + ROW.crewSize;
        const actRow = blockRow(i) + ROW.actualHours;
        row.getCell(1).value = String(phase.code);
        row.getCell(2).value = phase.description || '';
        row.getCell(3).value = disciplineLabel(phase);
        weeks.forEach((week, wi) => {
            const col = colLetter(firstWeekCol + wi);
            const dcol = colLetter(dataFirstWeekCol + wi);
            row.getCell(firstWeekCol + wi).value = {
                formula: `IF(${col}$${headerRow}>=$B$3,N(${DATA}!${dcol}${crewRow}),N(${DATA}!${dcol}${actRow})/$B$2)`,
            };
        });
        row.getCell(peakCol).value = { formula: `MAX(${D}${row.number}:${L}${row.number})` };
        for (let c = 1; c <= peakCol; c++) {
            const cell = row.getCell(c);
            cell.font = { ...plain, color: { argb: c <= 2 ? GREY_TEXT : INK } };
            cell.border = BORDER;
            if (c >= firstWeekCol) { cell.numFmt = FTE_FMT; cell.alignment = { horizontal: 'center' }; }
        }
    });

    // Total
    const total = ws.getRow(totalRow);
    total.getCell(2).value = 'Total Manpower';
    for (let c = firstWeekCol; c <= peakCol; c++) {
        const col = colLetter(c);
        total.getCell(c).value = { formula: `SUM(${col}${firstPhaseRow}:${col}${lastPhaseRow})` };
        total.getCell(c).numFmt = FTE_FMT;
        total.getCell(c).alignment = { horizontal: 'center' };
    }
    for (let c = 1; c <= peakCol; c++) { total.getCell(c).font = bold; total.getCell(c).border = BORDER; }

    // Rollup by discipline
    const disciplines = [];
    (phases || []).forEach((p) => { const d = disciplineLabel(p); if (!disciplines.includes(d)) disciplines.push(d); });
    const rollTitleRow = totalRow + 2;
    const rollHeaderRow = rollTitleRow + 1;
    const rollFirstRow = rollHeaderRow + 1;
    const rollLastRow = rollFirstRow + Math.max(disciplines.length, 1) - 1;
    const rollTotalRow = rollLastRow + 1;
    const checkRow = rollTotalRow + 1;
    ws.getCell(rollTitleRow, 1).value = 'Rollup by Discipline (for chart)';
    ws.getCell(rollTitleRow, 1).font = bold;
    const rh = ws.getRow(rollHeaderRow);
    rh.values = ['Discipline', '', '', ...weeks];
    for (let c = 1; c <= lastWeekCol; c++) headerStyle(rh.getCell(c));
    rh.height = 30;
    disciplines.forEach((d, i) => {
        const row = ws.getRow(rollFirstRow + i);
        row.getCell(1).value = d;
        row.getCell(1).font = plain;
        row.getCell(1).border = BORDER;
        weeks.forEach((_, wi) => {
            const col = colLetter(firstWeekCol + wi);
            const cell = row.getCell(firstWeekCol + wi);
            cell.value = { formula: `SUMIF($C$${firstPhaseRow}:$C$${lastPhaseRow},$A${row.number},${col}$${firstPhaseRow}:${col}$${lastPhaseRow})` };
            cell.numFmt = FTE_FMT; cell.font = plain; cell.border = BORDER; cell.alignment = { horizontal: 'center' };
        });
    });
    const rt = ws.getRow(rollTotalRow), ck = ws.getRow(checkRow);
    rt.getCell(1).value = 'Total'; rt.getCell(1).font = bold;
    ck.getCell(1).value = 'Check vs detail (should be 0)'; ck.getCell(1).font = plain;
    weeks.forEach((_, wi) => {
        const col = colLetter(firstWeekCol + wi);
        rt.getCell(firstWeekCol + wi).value = { formula: `SUM(${col}${rollFirstRow}:${col}${rollLastRow})` };
        ck.getCell(firstWeekCol + wi).value = { formula: `${col}${rollTotalRow}-${col}${totalRow}` };
        [rt, ck].forEach((r) => { const cell = r.getCell(firstWeekCol + wi); cell.numFmt = FTE_FMT; cell.alignment = { horizontal: 'center' }; cell.border = BORDER; });
        rt.getCell(firstWeekCol + wi).font = bold; ck.getCell(firstWeekCol + wi).font = plain;
    });

    ws.getColumn(1).width = 13; ws.getColumn(2).width = 27; ws.getColumn(3).width = 22;
    weeks.forEach((_, i) => { ws.getColumn(firstWeekCol + i).width = 10.5; });
    ws.getColumn(peakCol).width = 8;
    ws.views = [{ state: 'frozen', xSplit: 3, ySplit: headerRow, topLeftCell: `${D}${firstPhaseRow}` }];
    if (weeks.length) {
        ws.addConditionalFormatting({
            ref: `${D}${headerRow}:${L}${checkRow}`,
            rules: [{ type: 'expression', priority: 1, formulae: [`${D}$${headerRow}=$B$3`], style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFDDEBF7' } } } }],
        });
    }

    // The charts, anchored under the rollup. The refs are what Excel reads.
    const chartTop = checkRow + 2;
    const catRef = `'${MANPOWER_SHEET_NAME}'!$${D}$${rollHeaderRow}:$${L}$${rollHeaderRow}`;
    return {
        sheetName: MANPOWER_SHEET_NAME,
        charts: [
            {
                type: 'area', title: 'Manpower Curve by Discipline (FTE / week)', xTitle: 'Week Starting', yTitle: 'Crew (FTE)',
                catRef,
                series: disciplines.map((d, i) => ({
                    nameRef: `'${MANPOWER_SHEET_NAME}'!$A$${rollFirstRow + i}`,
                    valRef: `'${MANPOWER_SHEET_NAME}'!$${D}$${rollFirstRow + i}:$${L}$${rollFirstRow + i}`,
                    color: SERIES_COLORS[i % SERIES_COLORS.length],
                })),
                anchor: { fromCol: 3, fromRow: chartTop, toCol: 25, toRow: chartTop + 28 },
            },
            {
                type: 'line', title: 'Total Manpower (FTE / week)', xTitle: 'Week Starting', yTitle: 'Crew (FTE)',
                catRef,
                series: [{ name: 'Total Manpower', valRef: `'${MANPOWER_SHEET_NAME}'!$${D}$${rollTotalRow}:$${L}$${rollTotalRow}`, color: SERIES_COLORS[0] }],
                anchor: { fromCol: 25, fromRow: chartTop, toCol: 40, toRow: chartTop + 28 },
            },
        ],
    };
};

// ---------------------------------------------------------------------------
// Charts. ExcelJS writes no charts, so the workbook is written, reopened as
// a zip and the chart, drawing and relationship parts are added by hand.

const esc = (t) => String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const C_NS = 'xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"';

const richTitle = (text, size) => `<c:title><c:tx><c:rich><a:bodyPr/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="${size}" b="1"/></a:pPr><a:r><a:rPr lang="en-US" sz="${size}" b="1"/><a:t>${esc(text)}</a:t></a:r></a:p></c:rich></c:tx><c:overlay val="0"/></c:title>`;
const seriesTx = (s) => s.nameRef
    ? `<c:tx><c:strRef><c:f>${esc(s.nameRef)}</c:f></c:strRef></c:tx>`
    : `<c:tx><c:v>${esc(s.name || '')}</c:v></c:tx>`;
const seriesData = (s, catRef) => `<c:cat><c:strRef><c:f>${esc(catRef)}</c:f></c:strRef></c:cat><c:val><c:numRef><c:f>${esc(s.valRef)}</c:f></c:numRef></c:val>`;
const axes = (xTitle, yTitle) =>
    `<c:catAx><c:axId val="10"/><c:scaling><c:orientation val="minMax"/></c:scaling><c:delete val="0"/><c:axPos val="b"/>${richTitle(xTitle, 900)}<c:numFmt formatCode="General" sourceLinked="1"/><c:majorTickMark val="out"/><c:minorTickMark val="none"/><c:tickLblPos val="low"/><c:txPr><a:bodyPr rot="-2700000" vert="horz"/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="800"/></a:pPr><a:endParaRPr lang="en-US"/></a:p></c:txPr><c:crossAx val="20"/><c:crosses val="autoZero"/><c:auto val="1"/><c:lblAlgn val="ctr"/><c:lblOffset val="100"/><c:noMultiLvlLbl val="0"/></c:catAx>` +
    `<c:valAx><c:axId val="20"/><c:scaling><c:orientation val="minMax"/><c:min val="0"/></c:scaling><c:delete val="0"/><c:axPos val="l"/><c:majorGridlines><c:spPr><a:ln w="9525"><a:solidFill><a:srgbClr val="E5E5E5"/></a:solidFill></a:ln></c:spPr></c:majorGridlines>${richTitle(yTitle, 900)}<c:numFmt formatCode="#,##0" sourceLinked="0"/><c:majorTickMark val="out"/><c:minorTickMark val="none"/><c:tickLblPos val="nextTo"/><c:txPr><a:bodyPr/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="800"/></a:pPr><a:endParaRPr lang="en-US"/></a:p></c:txPr><c:crossAx val="10"/><c:crosses val="autoZero"/><c:crossBetween val="midCat"/></c:valAx>`;

export const chartXml = (chart) => {
    const noLabels = `<c:dLbls><c:showLegendKey val="0"/><c:showVal val="0"/><c:showCatName val="0"/><c:showSerName val="0"/><c:showPercent val="0"/><c:showBubbleSize val="0"/></c:dLbls>`;
    const plot = chart.type === 'area'
        ? `<c:areaChart><c:grouping val="stacked"/><c:varyColors val="0"/>` +
            chart.series.map((s, i) => `<c:ser><c:idx val="${i}"/><c:order val="${i}"/>${seriesTx(s)}<c:spPr><a:solidFill><a:srgbClr val="${s.color}"/></a:solidFill><a:ln w="6350"><a:solidFill><a:srgbClr val="FFFFFF"/></a:solidFill></a:ln></c:spPr>${seriesData(s, chart.catRef)}</c:ser>`).join('') +
            `${noLabels}<c:axId val="10"/><c:axId val="20"/></c:areaChart>`
        : `<c:lineChart><c:grouping val="standard"/><c:varyColors val="0"/>` +
            chart.series.map((s, i) => `<c:ser><c:idx val="${i}"/><c:order val="${i}"/>${seriesTx(s)}<c:spPr><a:ln w="28575" cap="rnd"><a:solidFill><a:srgbClr val="${s.color}"/></a:solidFill><a:round/></a:ln></c:spPr><c:marker><c:symbol val="none"/></c:marker>${seriesData(s, chart.catRef)}<c:smooth val="0"/></c:ser>`).join('') +
            `${noLabels}<c:marker val="1"/><c:axId val="10"/><c:axId val="20"/></c:lineChart>`;
    const legend = chart.series.length > 1 ? `<c:legend><c:legendPos val="r"/><c:overlay val="0"/><c:txPr><a:bodyPr/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="800"/></a:pPr><a:endParaRPr lang="en-US"/></a:p></c:txPr></c:legend>` : '';
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
        `<c:chartSpace ${C_NS}><c:roundedCorners val="0"/><c:chart>${richTitle(chart.title, 1200)}<c:autoTitleDeleted val="0"/><c:plotArea><c:layout/>${plot}${axes(chart.xTitle, chart.yTitle)}</c:plotArea>${legend}<c:plotVisOnly val="1"/><c:dispBlanksAs val="zero"/></c:chart><c:spPr><a:solidFill><a:srgbClr val="FFFFFF"/></a:solidFill><a:ln><a:solidFill><a:srgbClr val="E5E5E5"/></a:solidFill></a:ln></c:spPr></c:chartSpace>`;
};

const drawingXml = (charts) =>
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">` +
    charts.map((ch, i) => {
        const a = ch.anchor;
        return `<xdr:twoCellAnchor><xdr:from><xdr:col>${a.fromCol}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${a.fromRow}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:from>` +
            `<xdr:to><xdr:col>${a.toCol}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${a.toRow}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:to>` +
            `<xdr:graphicFrame macro=""><xdr:nvGraphicFramePr><xdr:cNvPr id="${i + 2}" name="Chart ${i + 1}"/><xdr:cNvGraphicFramePr/></xdr:nvGraphicFramePr>` +
            `<xdr:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/></xdr:xfrm><a:graphic><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/chart">` +
            `<c:chart xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:id="rId${i + 1}"/>` +
            `</a:graphicData></a:graphic></xdr:graphicFrame><xdr:clientData/></xdr:twoCellAnchor>`;
    }).join('') +
    `</xdr:wsDr>`;

const RELS_NS = 'http://schemas.openxmlformats.org/package/2006/relationships';
const relsXml = (rels) => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="${RELS_NS}">` +
    rels.map(r => `<Relationship Id="${r.id}" Type="${r.type}" Target="${r.target}"/>`).join('') + `</Relationships>`;

// Writes the workbook to an ArrayBuffer with the sheet's charts in it.
export const writeBurnToolPhasedXlsx = async (wb) => {
    const buffer = await wb.xlsx.writeBuffer();
    const spec = wb.burnToolCharts;
    if (!spec || !spec.charts?.length) return buffer;
    const zip = await JSZip.loadAsync(buffer);

    // The sheet's part, through workbook.xml and its rels
    const workbookXml = await zip.file('xl/workbook.xml').async('string');
    const sheetTag = (workbookXml.match(/<sheet\b[^>]*>/g) || []).find(t => (t.match(/name="([^"]*)"/) || [])[1] === esc(spec.sheetName));
    const rId = sheetTag && (sheetTag.match(/r:id="([^"]+)"/) || [])[1];
    const wbRels = await zip.file('xl/_rels/workbook.xml.rels').async('string');
    const relTag = rId && (wbRels.match(/<Relationship\b[^>]*>/g) || []).find(t => t.includes(`Id="${rId}"`));
    const target = relTag && (relTag.match(/Target="([^"]+)"/) || [])[1];
    if (!target) throw new Error(`Sheet "${spec.sheetName}" was not found in the workbook`);
    const sheetPath = target.startsWith('/') ? target.slice(1) : `xl/${target}`;
    const sheetRelsPath = sheetPath.replace(/worksheets\/([^/]+)$/, 'worksheets/_rels/$1.rels');

    // Parts
    const drawingNo = Object.keys(zip.files).filter(f => /^xl\/drawings\/drawing\d+\.xml$/.test(f)).length + 1;
    const chartStart = Object.keys(zip.files).filter(f => /^xl\/charts\/chart\d+\.xml$/.test(f)).length + 1;
    const chartNames = spec.charts.map((_, i) => `chart${chartStart + i}.xml`);
    spec.charts.forEach((ch, i) => zip.file(`xl/charts/${chartNames[i]}`, chartXml(ch)));
    zip.file(`xl/drawings/drawing${drawingNo}.xml`, drawingXml(spec.charts));
    zip.file(`xl/drawings/_rels/drawing${drawingNo}.xml.rels`, relsXml(chartNames.map((nm, i) => ({
        id: `rId${i + 1}`, type: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/chart', target: `../charts/${nm}`,
    }))));

    // The sheet points at the drawing
    const existingRels = zip.file(sheetRelsPath) ? await zip.file(sheetRelsPath).async('string') : null;
    const drawingRelId = `rIdDrawing${drawingNo}`;
    const drawingRel = `<Relationship Id="${drawingRelId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="../drawings/drawing${drawingNo}.xml"/>`;
    zip.file(sheetRelsPath, existingRels
        ? existingRels.replace('</Relationships>', `${drawingRel}</Relationships>`)
        : `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="${RELS_NS}">${drawingRel}</Relationships>`);
    let sheetXml = await zip.file(sheetPath).async('string');
    const drawingTag = `<drawing r:id="${drawingRelId}"/>`;
    const at = ['<legacyDrawing', '<tableParts', '<extLst', '</worksheet>'].map(t => sheetXml.indexOf(t)).filter(i => i >= 0).sort((a, b) => a - b)[0];
    sheetXml = sheetXml.slice(0, at) + drawingTag + sheetXml.slice(at);
    const rootTag = sheetXml.slice(sheetXml.indexOf('<worksheet'), sheetXml.indexOf('>', sheetXml.indexOf('<worksheet')) + 1);
    if (!/xmlns:r=/.test(rootTag)) {
        sheetXml = sheetXml.replace('<worksheet ', '<worksheet xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" ');
    }
    zip.file(sheetPath, sheetXml);

    // Content types
    let types = await zip.file('[Content_Types].xml').async('string');
    const overrides = [`<Override PartName="/xl/drawings/drawing${drawingNo}.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/>`]
        .concat(chartNames.map(nm => `<Override PartName="/xl/charts/${nm}" ContentType="application/vnd.openxmlformats-officedocument.drawingml.chart+xml"/>`));
    types = types.replace('</Types>', overrides.join('') + '</Types>');
    zip.file('[Content_Types].xml', types);

    return zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' });
};
